import type { ChangeEvent } from 'react';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { yupResolver } from '@hookform/resolvers/yup';
import AddIcon from '@mui/icons-material/Add';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  Typography,
  RadioGroup,
  FormControl,
  Radio,
  FormControlLabel,
  FormHelperText,
  Divider
} from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { downloadFileFromS3 } from '../../api/aws/s3';
import { editMedia3, mediasRef } from '../../api/firebase/firestore/media';
import { mediaServiceShema } from '../../api/firebase/firestore/media/validation';
import { usersRef } from '../../api/firebase/firestore/user';
import {
  productChoice,
  paymentChoice,
  compensationChoice,
  guaranteeChoice
} from '../../constants/CheckboxList';
import {
  black,
  blackText,
  blueGrayText,
  cardBorder,
  primarycolor,
  gray,
  primaryButtonBackground,
  primaryButton
} from '../../constants/Colors';
import {
  buttonFont,
  cardContentPadding,
  cardSize,
  formWideMb,
  mypageHeader,
  mypageHeaderTitle,
  textFontSize,
  textMb,
  titleFontSize,
  titleMb
} from '../../constants/Location';
import { publisherMediaRegisterEditSteps } from '../../constants/RegisterStep';
import { mediaBudgetList } from '../../constants/Select';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import CustomCheckbox from './CustomCheckbox';
import CustomFileDownload from './CustomFileDownload';
import CustomLoading from './CustomLoading';
import CustomSelectField from './CustomSelectField';
import CustomSnackbar from './CustomSnackbar';
import CustomStepper from './CustomStepper';
import CustomTextfield from './CustomTextfield';

import type { MediaType } from '../../api/firebase/firestore/media/mediaType';

const styles = {
  autoSize: {
    flexGrow: 1
  },
  buttonFont,
  cardBorder,
  cardContentPadding,
  cardSize,
  circle: {
    bgcolor: '#DBDBE0',
    borderRadius: '50%',
    height: '64px',
    marginBottom: '10px',
    width: '64px'
  },
  divider: {
    backgroundColor: '#DBDBE0',
    color: black
  },
  fileLabel: {
    color: black,
    fonstSize: '12px',
    margin: '8px 0'
  },
  flex: {
    display: 'flex'
  },
  formWideMb,
  grayPlaceholderText: {
    color: '#757575',
    fontFamily: 'Noto Sans JP,sans-serif',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '1.5',
    marginBottom: '10px'
  },
  helper: {
    '@media screen and (max-width:480px)': {
      marginLeft: 0
    }
  },
  isRequiredText: {
    color: '#BD0017',
    fontFamily: 'Noto Sans JP,sans-serif',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '1.5',
    margin: '0',
    marginLeft: '10px'
  },
  issueDescription: {
    color: blueGrayText,
    fontSize: '14px',
    marginBottom: '24px'
  },
  label: {
    fontSize: '12px'
  },
  mypageHeader,
  mypageHeaderTitle,
  nextButton: {
    '@media screen and (max-width: 480px)': {
      display: 'none'
    }
  },
  nextButtonSp: {
    '@media screen and (min-width: 481px)': {
      display: 'none'
    }
  },
  radiomarginLeft: {
    marginLeft: '5px'
  },
  required: {
    color: primarycolor,
    fontSize: '12px',
    marginLeft: '10px'
  },
  skipButton: {
    height: '36px',
    width: '140px'
  },
  submit: {
    marginBottom: 0,
    padding: 0
  },
  submitButton: {
    '@media screen and (max-width: 960px)': {
      height: '42px'
    },
    height: '42px'
  },
  submitCol: {
    columnGap: '8px'
  },
  text: {
    blackText,
    fontSize: '14px',
    marginBottom: '10px'
  },
  textFontSize,
  textMb,
  title: {
    color: blackText,
    fontSize: titleFontSize
  },
  titleMb,
  topMargin: {
    marginTop: '24px'
  },
  undoButton: {
    '&:hover': {
      borderColor: gray,
      opacity: 0.7
    },
    border: `1px solid ${gray}`,
    color: gray,
    height: '42px',
    marginRight: '16px'
  },
  uploadButton: {
    '&:hover': {
      backgroundColor: black,
      boxShadow: 'none'
    },
    backgroundColor: black,
    boxShadow: 'none',
    height: '36px',
    mb: '8px',
    minWidth: '157px'
  }
};

export interface MediaRegisterServiceType {
  adType: boolean[];
  adTypeOther: string | null;
  holdingDataFlg: string | null;
  holdingData: string | null;
  paymentFlg: string | null;
  payment: boolean[];
  budget: number;
  dueDate: string;
  guaranteeFlg: boolean[] | null;
  compensation: boolean[];
  otherMaterialUrl: string;
  otherStrength: string | null;
  seasonalMenuMaterialUrl: string;
  seasonalMenuMaterialText: string | null;
  similarMediaName1: string | null;
  similarMediaComparison1: string | null;
  similarMediaName2: string | null;
  similarMediaComparison2: string | null;
  mediaMaterialUrl: string;
  mediaMaterialUpdateCycle: number | null;
}

const MediaRegisterServiceEditCard = (): React.ReactElement => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const steps = publisherMediaRegisterEditSteps();
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);
  const [open, setOpen] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string>('');
  const [firstSubmit, setFirstSubmit] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [mediaCompanyId, setMediaCompanyId] = useState<string>('');
  const guaranteeList = guaranteeChoice();

  useEffect(() => {
    if (!currentUser) {
      setOpen(true);
      setErrorText(t('form.loginError'));
      setTimeout(() => {
        navigate('/auth/login');
      }, 6000);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    void (async () => {
      if (mediaCompanyId) {
        const userRef = doc(usersRef, currentUser?.uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.data();
        if (userData) {
          const userCompany = userData.companyId as string;
          if (userCompany !== mediaCompanyId) {
            navigate('/auth/login');
          }
        }
      }
    })();
  }, [mediaCompanyId]);

  const {
    control,
    watch,
    register,
    setValue,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<MediaRegisterServiceType>({
    defaultValues: {
      budget: 0,
      guaranteeFlg: [],
      holdingDataFlg: null,
      mediaMaterialUpdateCycle: null,
      paymentFlg: null
    },
    resolver: yupResolver(mediaServiceShema)
  });

  const createCheckboxBooleanArray = (
    data?: number[] | number,
    length?: number
  ): boolean[] => {
    if (!length || !data) return [];

    const newArray = Array(length).fill(false) as boolean[];

    if (typeof data === 'number') {
      newArray[data] = true;
    } else {
      data.forEach((element: number) => {
        newArray[element] = true;
      });
    }

    return newArray;
  };

  useEffect(() => {
    void (async () => {
      const mediaRef = doc(mediasRef, id);
      const mediaDoc = await getDoc(mediaRef);
      const mediaData = mediaDoc.data() as MediaType;
      setMediaCompanyId(mediaData?.companyId);
      reset({
        adType: createCheckboxBooleanArray(mediaData?.adType, 6),
        adTypeOther: mediaData?.adOption,
        budget: mediaData?.budget,
        compensation: [
          undefined,
          mediaData?.editorFlg,
          mediaData?.guarantee,
          mediaData?.reportFlg,
          mediaData?.castingFlg,
          mediaData?.secondaryUseFlg,
          mediaData?.researchSchemeFlg
        ],
        dueDate: mediaData?.dueDate,
        guaranteeFlg: createCheckboxBooleanArray(mediaData?.guaranteeFlg, 3),
        holdingData: mediaData?.holdingData,
        holdingDataFlg: mediaData?.holdingDataFlg ? '1' : '2',
        mediaMaterialUpdateCycle: mediaData?.mediaMaterialUpdateCycle,
        otherStrength: mediaData?.otherStrength,
        payment: createCheckboxBooleanArray(mediaData?.payment, 8),
        paymentFlg: mediaData?.paymentFlg ? '1' : '2',
        seasonalMenuMaterialText: mediaData?.seasonalMenuMaterialText,
        similarMediaComparison1: mediaData?.similarMedia[0]?.comparison,
        similarMediaComparison2: mediaData?.similarMedia[1]?.comparison,
        similarMediaName1: mediaData?.similarMedia[0]?.mediaName,
        similarMediaName2: mediaData?.similarMedia[1]?.mediaName
      });
      if (mediaData.mediaMaterialFile[0]) {
        setMaterialFileNames((fileNames) => [
          ...fileNames,
          mediaData.mediaMaterialFile[0]
        ]);
        setMaterialPreview((preview) => [...preview, '']);
        setMaterialFiles((files) => [...files, mediaData.mediaMaterialFile[0]]);
      }

      if (mediaData.seasonalMenuMaterialFile[0]) {
        setSeasonalFileNames((fileNames) => [
          ...fileNames,
          mediaData.seasonalMenuMaterialFile[0]
        ]);
        setSeasonalPreview((preview) => [...preview, '']);
        setSeasonalFiles((files) => [
          ...files,
          mediaData.seasonalMenuMaterialFile[0]
        ]);
      }

      if (mediaData.otherMaterialFile[0]) {
        setOtherMaterialFileNames((fileNames) => [
          ...fileNames,
          mediaData.otherMaterialFile[0]
        ]);
        setOtherMaterialPreview((preview) => [...preview, '']);
        setOtherMaterialFiles((files) => [
          ...files,
          mediaData.otherMaterialFile[0]
        ]);
      }
    })();
  }, []);

  useEffect(() => {
    watch('adTypeOther') && setValue('adType.99', true);
  }, [watch('adTypeOther')]);

  useEffect(() => {
    !watch('adType.99') && setValue('adTypeOther', null);
  }, [watch('adType.99')]);

  useEffect(() => {
    if (watch('holdingDataFlg') !== '1') {
      setValue('holdingData', null);
    }
  }, [watch('holdingDataFlg')]);

  useEffect(() => {
    !watch('compensation.2') && setValue('guaranteeFlg', null);
  }, [watch('compensation.2')]);

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('adType') === null ||
        (watch('adType.99') && Boolean(!watch('adTypeOther'))) ||
        watch('holdingDataFlg') === null ||
        watch('paymentFlg') === null ||
        watch('budget') === null ||
        watch('mediaMaterialUrl') === null ||
        '' ||
        Boolean(errors.adType) ||
        (watch('adType.99') && Boolean(errors.adTypeOther)) ||
        (seasonalfiles.length > 0 &&
          (watch('seasonalMenuMaterialText') === null ||
            watch('seasonalMenuMaterialText') === '' ||
            watch('seasonalMenuMaterialText') === undefined)) ||
        Boolean(errors.holdingDataFlg) ||
        (watch('holdingDataFlg') === '1' && Boolean(errors.holdingData)) ||
        Boolean(errors.paymentFlg) ||
        Boolean(errors.payment) ||
        Boolean(errors.budget) ||
        Boolean(errors.dueDate) ||
        Boolean(errors.compensation) ||
        Boolean(errors.otherStrength) ||
        Boolean(errors.similarMediaName1) ||
        Boolean(errors.similarMediaComparison1) ||
        Boolean(errors.similarMediaName2) ||
        Boolean(errors.similarMediaComparison2) ||
        Boolean(errors.mediaMaterialUrl) ||
        Boolean(errors.mediaMaterialUpdateCycle) ||
        (seasonalfiles.length > 0 &&
          Boolean(errors.seasonalMenuMaterialText)) ||
        (seasonalfiles.length > 0 && Boolean(errorSeasonalTextRequired)) ||
        Boolean(errorNumberOfMaterialFiles) ||
        Boolean(errorNumberOfSeasonalFiles) ||
        Boolean(errorNumberOfOtherMaterialFiles)
      );
    }
    return false;
  };

  const undoToIssue = async (): Promise<void> => {
    if (id) {
      setLoading(true);
      try {
        await editMedia3(watch, id);
        navigate(`/publisher/mypage/media/${id}/edit2`);
      } catch (e) {
        throwError(e);
      }
    }
  };

  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      if (id) {
        await editMedia3(
          watch,
          id,
          materialfiles,
          materialDeleteFiles,
          seasonalfiles,
          seasonalDeleteFiles,
          otherMaterialfiles,
          otherMaterialDeleteFiles
        );
        navigate(`/publisher/mypage/media/${id}/edit4`);
      }
    } catch (e) {
      throwError(e);
    }
  };

  // 媒体資料アップロード
  const [materialFileNames, setMaterialFileNames] = useState<string[]>([]);
  const [materialPreview, setMaterialPreview] = useState<string[]>([]);
  const [materialfiles, setMaterialFiles] = useState<Array<FileList | string>>(
    []
  );
  const [materialDeleteFiles, setMaterialDeleteFiles] = useState<string[]>([]);
  const [errorNumberOfMaterialFiles, setErrorNumberOfMaterialFiles] =
    useState<boolean>(false);

  const materialFileCheck = (): boolean => {
    if (materialFileNames.length !== 1) {
      setErrorNumberOfMaterialFiles(true);
    } else {
      setErrorNumberOfMaterialFiles(false);
    }
    return materialFileNames.length === 1;
  };

  useEffect(() => {
    if (firstSubmit) {
      materialFileCheck();
    }
  }, [materialFileNames]);

  const changeUploadMaterialFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file) {
      const blob = new Blob([file[0]]);
      setMaterialFileNames([...materialFileNames, file[0].name]);
      setMaterialPreview([
        ...materialPreview,
        window.URL.createObjectURL(blob)
      ]);
      setMaterialFiles([...materialfiles, file]);
    }
  };

  const deleteUploadMaterialFile = (i: number, name: string): void => {
    if (name.match(/medias\/.*\/file\//g)) {
      setMaterialDeleteFiles([...materialDeleteFiles, name]);
    }
    setMaterialFileNames(
      materialFileNames.filter((file, index) => index !== i)
    );
    setMaterialPreview(materialPreview.filter((file, index) => index !== i));
    setMaterialFiles(materialfiles.filter((file, index) => index !== i));
  };

  const downloadMaterialFile = (i: number, name: string): void => {
    if (!name.match(/medias\/.*\/file\//g)) {
      const url = materialPreview[i];
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.download = `${materialFileNames[i]}`;
      a.href = url;
      a.click();
    } else {
      void (async () => {
        await downloadFileFromS3(name);
      })();
    }
  };

  // シーズナル資料アップロード
  const [seasonalFileNames, setSeasonalFileNames] = useState<string[]>([]);
  const [seasonalPreview, setSeasonalPreview] = useState<string[]>([]);
  const [seasonalfiles, setSeasonalFiles] = useState<Array<FileList | string>>(
    []
  );
  const [seasonalDeleteFiles, setSeasonalDeleteFiles] = useState<string[]>([]);
  const [errorNumberOfSeasonalFiles, setErrorNumberOfSeasonalFiles] =
    useState<boolean>(false);

  const [errorSeasonalTextRequired, setErrorSeasonalTextRequired] =
    useState<boolean>(false);

  const seasonalFileCheck = (): boolean => {
    if (firstSubmit) {
      setErrorNumberOfSeasonalFiles(seasonalFileNames.length > 1);
    }
    return seasonalFileNames.length <= 1;
  };

  useEffect(() => {
    if (firstSubmit) {
      seasonalFileCheck();
    }
  }, [seasonalFileNames]);

  useEffect(() => {
    setErrorSeasonalTextRequired(
      seasonalFileNames.length > 0 &&
        (watch('seasonalMenuMaterialText') === null ||
          watch('seasonalMenuMaterialText') === undefined ||
          watch('seasonalMenuMaterialText') === '')
    );
    setFirstSubmit(
      seasonalFileNames.length > 0 &&
        (watch('seasonalMenuMaterialText') === null ||
          watch('seasonalMenuMaterialText') === undefined ||
          watch('seasonalMenuMaterialText') === '')
    );
  }, [seasonalFileNames.length, watch('seasonalMenuMaterialText')]);

  const changeUploadSeasonalFile = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files;
    if (file) {
      const blob = new Blob([file[0]]);
      setSeasonalFileNames([...seasonalFileNames, file[0].name]);
      setSeasonalPreview([
        ...seasonalPreview,
        window.URL.createObjectURL(blob)
      ]);
      setSeasonalFiles([...seasonalfiles, file]);
    }
  };

  const deleteUploadSeasonalFile = (i: number, name: string): void => {
    if (name.match(/medias\/.*\/file\//g)) {
      setSeasonalDeleteFiles([...seasonalDeleteFiles, name]);
    }
    setSeasonalFileNames(
      seasonalFileNames.filter((file, index) => index !== i)
    );
    setSeasonalPreview(seasonalPreview.filter((file, index) => index !== i));
    setSeasonalFiles(seasonalfiles.filter((file, index) => index !== i));
  };

  const downloadSeasonalFile = (i: number, name: string): void => {
    if (!name.match(/medias\/.*\/file\//g)) {
      const url = seasonalPreview[i];
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.download = `${seasonalFileNames[i]}`;
      a.href = url;
      a.click();
    } else {
      void (async () => {
        await downloadFileFromS3(name);
      })();
    }
  };

  // その他資料アップロード
  const [otherMaterialFileNames, setOtherMaterialFileNames] = useState<
    string[]
  >([]);
  const [otherMaterialPreview, setOtherMaterialPreview] = useState<string[]>(
    []
  );
  const [otherMaterialfiles, setOtherMaterialFiles] = useState<
    Array<FileList | string>
  >([]);
  const [otherMaterialDeleteFiles, setOtherMaterialDeleteFiles] = useState<
    string[]
  >([]);
  const [errorNumberOfOtherMaterialFiles, setErrorNumberOfOtherMaterialFiles] =
    useState<boolean>(false);

  const otherMaterialFileCheck = (): boolean => {
    if (firstSubmit) {
      setErrorNumberOfOtherMaterialFiles(otherMaterialFileNames.length > 1);
    }
    return otherMaterialFileNames.length <= 1;
  };

  useEffect(() => {
    if (firstSubmit) {
      otherMaterialFileCheck();
    }
  }, [otherMaterialFileNames]);

  const changeUploadOtherMaterialFile = (
    e: ChangeEvent<HTMLInputElement>
  ): void => {
    const file = e.target.files;
    if (file) {
      const blob = new Blob([file[0]]);
      setOtherMaterialFileNames([...otherMaterialFileNames, file[0].name]);
      setOtherMaterialPreview([
        ...otherMaterialPreview,
        window.URL.createObjectURL(blob)
      ]);
      setOtherMaterialFiles([...otherMaterialfiles, file]);
    }
  };

  const deleteUploadOtherMaterialFile = (i: number, name: string): void => {
    if (name.match(/medias\/.*\/file\//g)) {
      setOtherMaterialDeleteFiles([...otherMaterialDeleteFiles, name]);
    }
    setOtherMaterialFileNames(
      otherMaterialFileNames.filter((file, index) => index !== i)
    );
    setOtherMaterialPreview(
      otherMaterialPreview.filter((file, index) => index !== i)
    );
    setOtherMaterialFiles(
      otherMaterialfiles.filter((file, index) => index !== i)
    );
  };

  const downloadOtherMaterialFile = (i: number, name: string): void => {
    if (!name.match(/medias\/.*\/file\//g)) {
      const url = otherMaterialPreview[i];
      const a = document.createElement('a');
      document.body.appendChild(a);
      a.download = `${otherMaterialFileNames[i]}`;
      a.href = url;
      a.click();
    } else {
      void (async () => {
        await downloadFileFromS3(name);
      })();
    }
  };

  const handleButtonClick = async (): Promise<void> => {
    setFirstSubmit(true);
    const materialFile = materialFileCheck();
    const seasonalFile = seasonalFileCheck();
    const otherMaterialFile = otherMaterialFileCheck();
    await handleSubmit(async () => {
      if (materialFile && seasonalFile && otherMaterialFile) {
        await onSubmit();
      }
    })();
  };

  return (
    <>
      <Typography
        component="div"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          position: 'static',
          width: '100%'
        }}
      >
        <Typography sx={styles.mypageHeader} component="div">
          <Typography sx={styles.mypageHeaderTitle}>
            {t('mypage.mediaRegister')}
          </Typography>
          <Button
            variant="contained"
            sx={[styles.nextButton, primaryButton]}
            disabled={buttonDisabledFlag()}
            onClick={handleButtonClick}
          >
            {t('publisherMediaRegisterEdit.toCase')}
          </Button>
          <Button
            variant="contained"
            sx={[styles.nextButtonSp, primaryButton]}
            disabled={buttonDisabledFlag()}
            onClick={handleButtonClick}
          >
            {t('publisherMediaRegisterEdit.spButton')}
          </Button>
        </Typography>

        <CustomLoading loading={loading} />
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          sx={{ marginBottom: '24px', marginTop: '24px' }}
        >
          <Grid>
            <Card sx={[styles.cardSize, styles.cardBorder]}>
              <CardContent sx={styles.cardContentPadding}>
                <CustomStepper steps={steps} activeStep={2} />
                <Typography
                  component="div"
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '16px'
                  }}
                >
                  <Typography sx={styles.title} component="div">
                    {t('publisherMediaRegisterEdit.step3')}
                  </Typography>
                </Typography>

                <CustomCheckbox
                  labelText={t('publisherMediaRegisterEdit.product')}
                  isRequired
                  requiredText={t('form.required')}
                  list={productChoice()}
                  control={control}
                  registerMediaService={register}
                  watchMediaService={watch}
                  error={Boolean(errors.adType)}
                  helperText={errors.adType?.message}
                  formMb="narrow"
                  name="adType"
                />
                <Typography component="div" sx={{ marginLeft: '25px' }}>
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        isRequired={false}
                        multiline
                        rows={3}
                        fieldType="textareaRow3"
                        width="other"
                        error={Boolean(errors.adTypeOther)}
                        helperText={errors.adTypeOther?.message}
                      />
                    )}
                    control={control}
                    name="adTypeOther"
                  />
                </Typography>
                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      isRequired={false}
                      labelText={t('publisherMediaRegisterEdit.menu')}
                      multiline
                      rows={7}
                      fieldType="textareaRow7"
                      width="article"
                      error={Boolean(errors.otherStrength)}
                      helperText={errors.otherStrength?.message}
                      guideText={t('guideText.registerMedia.otherStrength')}
                    />
                  )}
                  control={control}
                  name="otherStrength"
                />
                <Grid
                  sx={{
                    ...(watch('holdingDataFlg') === '2' && { mb: '14px' })
                  }}
                >
                  <Typography component="div" sx={styles.flex}>
                    <Typography sx={[styles.grayPlaceholderText, { mb: 0 }]}>
                      {t('publisherMediaRegisterEdit.data.label')}
                    </Typography>
                    <Typography sx={styles.isRequiredText}>
                      {t('form.required')}
                    </Typography>
                  </Typography>
                  <FormHelperText
                    sx={styles.helper}
                    error={Boolean(errors.holdingDataFlg)}
                  >
                    {errors.holdingDataFlg?.message}
                  </FormHelperText>
                  <Controller
                    render={({ field }) => (
                      <FormControl>
                        <RadioGroup
                          {...field}
                          row
                          aria-labelledby="demo-form-control-label-placement"
                          name="position"
                          sx={{
                            '&.MuiFormGroup-root': {
                              alignContent: 'flex-start',
                              marginBottom: '10px'
                            }
                          }}
                        >
                          <FormControlLabel
                            value={1}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('publisherMediaRegisterEdit.data.1')}
                            sx={blackText}
                          />
                          <FormControlLabel
                            value={2}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('publisherMediaRegisterEdit.data.2')}
                            sx={blackText}
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                    control={control}
                    name="holdingDataFlg"
                  />
                </Grid>

                {watch('holdingDataFlg') === '1' && (
                  <Controller
                    render={({ field }) => (
                      <CustomTextfield
                        {...field}
                        isRequired={false}
                        labelText={t('publisherMediaRegisterEdit.data.kind')}
                        width="other"
                        error={Boolean(errors.holdingData)}
                        helperText={errors.holdingData?.message}
                      />
                    )}
                    control={control}
                    name="holdingData"
                  />
                )}

                <Grid sx={{ mb: '14px' }}>
                  <Typography component="div" sx={styles.flex}>
                    <Typography sx={[styles.grayPlaceholderText, { mb: 0 }]}>
                      {t('publisherMediaRegisterEdit.ec.label')}
                    </Typography>
                    <Typography sx={styles.isRequiredText}>
                      {t('form.required')}
                    </Typography>
                  </Typography>
                  <FormHelperText
                    sx={styles.helper}
                    error={Boolean(errors.paymentFlg)}
                  >
                    {errors.paymentFlg?.message}
                  </FormHelperText>
                  <Controller
                    render={({ field }) => (
                      <FormControl>
                        <RadioGroup
                          {...field}
                          row
                          aria-labelledby="demo-form-control-label-placement"
                          name="position"
                          sx={{
                            '&.MuiFormGroup-root': {
                              alignContent: 'flex-start',
                              marginBottom: '10px'
                            }
                          }}
                        >
                          <FormControlLabel
                            value={1}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('publisherMediaRegisterEdit.ec.1')}
                            sx={blackText}
                          />
                          <FormControlLabel
                            value={2}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('publisherMediaRegisterEdit.ec.2')}
                            sx={blackText}
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                    control={control}
                    name="paymentFlg"
                  />
                </Grid>
                <Grid sx={{ mb: '24px' }}>
                  <CustomCheckbox
                    isRequired={false}
                    labelText={t('media.payment')}
                    list={paymentChoice()}
                    control={control}
                    registerMediaService={register}
                    watchMediaService={watch}
                    error={Boolean(errors.payment)}
                    helperText={errors.payment?.message}
                    formMb="narrow"
                    name="payment"
                  />
                </Grid>
                <Controller
                  render={({ field }) => (
                    <CustomSelectField
                      {...field}
                      labelText={t('media.budget')}
                      isRequired
                      requiredText={t('form.required')}
                      list={mediaBudgetList()}
                      error={Boolean(errors.budget)}
                      helperText={errors.budget?.message}
                    />
                  )}
                  control={control}
                  name="budget"
                />

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      labelText={t('publisherMediaRegisterEdit.schedule')}
                      isRequired={false}
                      width="article"
                      error={Boolean(errors.dueDate)}
                      helperText={errors.dueDate?.message}
                      guideText={t('publisherMediaRegisterEdit.scheduleText')}
                    />
                  )}
                  control={control}
                  name="dueDate"
                />

                <Grid sx={{ mb: '24px' }}>
                  <Typography component="div" sx={styles.flex}>
                    <Typography
                      sx={[styles.grayPlaceholderText, { mb: '4px' }]}
                    >
                      {t('media.supplementary')}
                    </Typography>
                    <Typography sx={styles.isRequiredText}>
                      {t('form.required')}
                    </Typography>
                  </Typography>
                  <Typography sx={[styles.grayPlaceholderText, { mb: 0 }]}>
                    {t('media.supplementaryDetail')}
                  </Typography>
                  <CustomCheckbox
                    isRequired={false}
                    list={compensationChoice()}
                    control={control}
                    registerMediaService={register}
                    watchMediaService={watch}
                    error={Boolean(errors.compensation)}
                    helperText={errors.compensation?.message}
                    formMb="narrow"
                    name="compensation"
                  />
                </Grid>

                {watch('compensation.2') && (
                  <Grid sx={{ mb: '24px' }}>
                    <Typography component="div" sx={styles.flex}>
                      <Typography
                        sx={[styles.grayPlaceholderText, { mb: '4px' }]}
                      >
                        {t('media.guaranteeFlg')}
                      </Typography>
                    </Typography>
                    <Typography sx={[styles.grayPlaceholderText, { mb: 0 }]}>
                      {t('media.guaranteeFlgDesc')}
                    </Typography>
                    <CustomCheckbox
                      isRequired={false}
                      list={guaranteeList}
                      control={control}
                      registerMediaService={register}
                      watchMediaService={watch}
                      error={Boolean(errors.guaranteeFlg)}
                      helperText={errors.guaranteeFlg?.message}
                      formMb="narrow"
                      name="guaranteeFlg"
                    />
                  </Grid>
                )}

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      labelText={t('publisherMediaRegisterEdit.comparison')}
                      isRequired
                      requiredText={t('form.required')}
                      width="article"
                      placeholder={t(
                        'publisherMediaRegisterEdit.compareService1'
                      )}
                      error={Boolean(errors.similarMediaName1)}
                      helperText={errors.similarMediaName1?.message}
                      formMb="narrow"
                      guideText={t('guideText.registerMedia.comparison')}
                    />
                  )}
                  control={control}
                  name="similarMediaName1"
                />

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      isRequired={false}
                      multiline
                      rows={7}
                      fieldType="textareaRow7"
                      width="article"
                      placeholder={t(
                        'publisherMediaRegisterEdit.differentPoint'
                      )}
                      error={Boolean(errors.similarMediaComparison1)}
                      helperText={errors.similarMediaComparison1?.message}
                    />
                  )}
                  control={control}
                  name="similarMediaComparison1"
                />

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      isRequired={false}
                      width="article"
                      placeholder={t(
                        'publisherMediaRegisterEdit.compareService2'
                      )}
                      error={Boolean(errors.similarMediaName2)}
                      helperText={errors.similarMediaName2?.message}
                      formMb="narrow"
                    />
                  )}
                  control={control}
                  name="similarMediaName2"
                />

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      isRequired={false}
                      multiline
                      rows={7}
                      fieldType="textareaRow7"
                      width="article"
                      placeholder={t(
                        'publisherMediaRegisterEdit.differentPoint'
                      )}
                      error={Boolean(errors.similarMediaComparison2)}
                      helperText={errors.similarMediaComparison2?.message}
                    />
                  )}
                  control={control}
                  name="similarMediaComparison2"
                />

                <Typography component="div" sx={{ marginBottom: formWideMb }}>
                  <Grid container flexDirection="row" columnGap="6px">
                    <Typography color={gray} sx={styles.label} component="p">
                      {t('media.mediaMaterialUrl')}
                    </Typography>
                    <Typography
                      color={primarycolor}
                      sx={styles.label}
                      component="p"
                    >
                      {t('form.required')}
                    </Typography>
                  </Grid>
                  <Typography sx={styles.fileLabel} component="p">
                    {t('media.fileUploadDesc')}
                  </Typography>
                  <Button
                    component="label"
                    variant="contained"
                    sx={styles.uploadButton}
                  >
                    <input
                      accept=".pdf, .ppt, .pptx, .potx, .pot, .odp, .ppsx, .pps, .pptm, .potm, .ppsm, .doc, .docx, .dotx, .dot, .docm, .dotm, .odt, .xls, .xlsx, .xltx, .xlt, .xlsm, .xlsb, .xltm, .prn, .dif, .slk, .xlam, .xla, .ods, .txt, .rtf, .csv, .tsv .jpg, .jpeg, .png, .gif, .svg, .webp"
                      type="file"
                      hidden
                      onChange={(value) => {
                        changeUploadMaterialFile(value);
                      }}
                    />
                    <AddIcon sx={{ color: '#FFF' }} />
                    {t('orientation.imageUploadButton')}
                  </Button>
                  <FormHelperText
                    sx={styles.helper}
                    error={errorNumberOfMaterialFiles}
                  >
                    {errorNumberOfMaterialFiles &&
                      materialFileNames.length > 1 &&
                      t('validation.error.numberOfFile')}
                    {errorNumberOfMaterialFiles &&
                      materialFileNames.length === 0 &&
                      t('validation.error.required')}
                  </FormHelperText>
                  <Typography component="div" sx={{ mb: '32px', mt: '8px' }}>
                    <Divider light sx={[styles.divider]} />
                    {materialFileNames?.map((fileName, index) => (
                      <CustomFileDownload
                        download={downloadMaterialFile}
                        deleteFunc={deleteUploadMaterialFile}
                        key={index}
                        fileName={fileName}
                        index={index}
                      />
                    ))}
                  </Typography>
                </Typography>

                <Typography component="div" sx={{ marginBottom: formWideMb }}>
                  <Typography color={gray} sx={styles.label} component="p">
                    {t('media.seasonalMenuMaterialUrl')}
                  </Typography>
                  <Typography sx={styles.fileLabel} component="p">
                    {t('media.fileUploadDesc')}
                  </Typography>
                  <Button
                    component="label"
                    variant="contained"
                    sx={styles.uploadButton}
                  >
                    <input
                      accept=".pdf, .ppt, .pptx, .potx, .pot, .odp, .ppsx, .pps, .pptm, .potm, .ppsm, .doc, .docx, .dotx, .dot, .docm, .dotm, .odt, .xls, .xlsx, .xltx, .xlt, .xlsm, .xlsb, .xltm, .prn, .dif, .slk, .xlam, .xla, .ods, .txt, .rtf, .csv, .tsv .jpg, .jpeg, .png, .gif, .svg, .webp"
                      type="file"
                      hidden
                      onChange={(value) => {
                        changeUploadSeasonalFile(value);
                      }}
                    />
                    <AddIcon sx={{ color: '#FFF' }} />
                    {t('orientation.imageUploadButton')}
                  </Button>
                  <FormHelperText
                    sx={styles.helper}
                    error={errorNumberOfSeasonalFiles}
                  >
                    {errorNumberOfSeasonalFiles &&
                      t('validation.error.numberOfFile')}
                  </FormHelperText>
                  <Typography component="div" sx={{ mb: '32px', mt: '8px' }}>
                    <Divider light sx={[styles.divider]} />
                    {seasonalFileNames?.map((fileName, index) => (
                      <CustomFileDownload
                        download={downloadSeasonalFile}
                        deleteFunc={deleteUploadSeasonalFile}
                        key={index}
                        fileName={fileName}
                        index={index}
                      />
                    ))}
                  </Typography>
                </Typography>

                <Controller
                  render={({ field }) => (
                    <CustomTextfield
                      {...field}
                      labelText={t('media.seasonalMenuMaterialText')}
                      isRequired={false}
                      multiline
                      rows={2}
                      fieldType="textareaRow2"
                      width="article"
                      error={
                        errorSeasonalTextRequired ||
                        Boolean(errors.seasonalMenuMaterialText)
                      }
                      helperText={
                        errorSeasonalTextRequired
                          ? t('validation.error.required')
                          : errors.seasonalMenuMaterialText?.message
                      }
                      guideText={t('guideText.registerMedia.salestolk')}
                    />
                  )}
                  control={control}
                  name="seasonalMenuMaterialText"
                />

                <Typography component="div" sx={{ marginBottom: formWideMb }}>
                  <Typography color={gray} sx={styles.label} component="p">
                    {t('media.otherMaterialUrl')}
                  </Typography>
                  <Typography sx={styles.fileLabel} component="p">
                    {t('media.fileUploadDesc')}
                  </Typography>
                  <Button
                    component="label"
                    variant="contained"
                    sx={styles.uploadButton}
                  >
                    <input
                      accept=".pdf, .ppt, .pptx, .potx, .pot, .odp, .ppsx, .pps, .pptm, .potm, .ppsm, .doc, .docx, .dotx, .dot, .docm, .dotm, .odt, .xls, .xlsx, .xltx, .xlt, .xlsm, .xlsb, .xltm, .prn, .dif, .slk, .xlam, .xla, .ods, .txt, .rtf, .csv, .tsv .jpg, .jpeg, .png, .gif, .svg, .webp"
                      type="file"
                      hidden
                      onChange={(value) => {
                        changeUploadOtherMaterialFile(value);
                      }}
                    />
                    <AddIcon sx={{ color: '#FFF' }} />
                    {t('orientation.imageUploadButton')}
                  </Button>
                  <FormHelperText
                    sx={styles.helper}
                    error={errorNumberOfOtherMaterialFiles}
                  >
                    {errorNumberOfOtherMaterialFiles &&
                      t('validation.error.numberOfFile')}
                  </FormHelperText>
                  <Typography component="div" sx={{ mb: '32px', mt: '8px' }}>
                    <Divider light sx={[styles.divider]} />
                    {otherMaterialFileNames?.map((fileName, index) => (
                      <CustomFileDownload
                        download={downloadOtherMaterialFile}
                        deleteFunc={deleteUploadOtherMaterialFile}
                        key={index}
                        fileName={fileName}
                        index={index}
                      />
                    ))}
                  </Typography>
                </Typography>

                <Typography align="left" component="div" sx={{ mb: '16px' }}>
                  <Typography
                    component="div"
                    sx={[styles.flex, styles.topMargin]}
                  >
                    <Typography sx={[styles.grayPlaceholderText, { mb: 0 }]}>
                      {t('media.mediaMaterialUpdateCycle')}
                    </Typography>
                  </Typography>
                  <FormHelperText
                    sx={styles.helper}
                    error={Boolean(errors.mediaMaterialUpdateCycle)}
                  >
                    {errors.mediaMaterialUpdateCycle?.message}
                  </FormHelperText>
                  <Controller
                    render={({ field }) => (
                      <FormControl>
                        <RadioGroup
                          {...field}
                          aria-labelledby="demo-form-control-label-placement"
                          name="position"
                          row
                          sx={{
                            '&.MuiFormGroup-root': {
                              alignContent: 'flex-start',
                              flexDirection: 'column',
                              marginBottom: '10px'
                            }
                          }}
                        >
                          <FormControlLabel
                            value={1}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('media.mediaMaterialUpdateCycleOption.1')}
                            sx={blackText}
                          />
                          <FormControlLabel
                            value={2}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('media.mediaMaterialUpdateCycleOption.2')}
                            sx={blackText}
                          />
                          <FormControlLabel
                            value={3}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('media.mediaMaterialUpdateCycleOption.3')}
                            sx={blackText}
                          />
                          <FormControlLabel
                            value={99}
                            control={<Radio sx={{ color: gray }} />}
                            label={t('media.mediaMaterialUpdateCycleOption.99')}
                            sx={blackText}
                          />
                        </RadioGroup>
                      </FormControl>
                    )}
                    control={control}
                    name="mediaMaterialUpdateCycle"
                  />
                </Typography>

                <CardActions sx={[styles.textMb, styles.submit]}>
                  <Grid container sx={styles.submitCol}>
                    <Grid item>
                      <Button
                        disabled={buttonDisabledFlag()}
                        variant="outlined"
                        fullWidth
                        onClick={() => {
                          void (async () => {
                            setFirstSubmit(true);
                            await handleSubmit(
                              async () => await undoToIssue()
                            )();
                          })();
                        }}
                        sx={styles.undoButton}
                      >
                        <Typography sx={{ fontSize: '14px' }}>
                          {t('passwordReset.back')}
                        </Typography>
                      </Button>
                    </Grid>
                    <Grid item flex={1}>
                      <Button
                        disabled={buttonDisabledFlag()}
                        fullWidth
                        sx={[primaryButtonBackground, styles.submitButton]}
                        onClick={handleButtonClick}
                      >
                        <Typography
                          component="div"
                          color="white"
                          sx={{ fontSize: 15 }}
                        >
                          {t('publisherMediaRegisterEdit.toCase')}
                        </Typography>
                      </Button>
                    </Grid>
                  </Grid>
                </CardActions>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <CustomSnackbar
          open={open}
          autoHideDuration={6000}
          onClose={() => setOpen(false)}
          text={errorText}
        />
      </Typography>
    </>
  );
};
export default MediaRegisterServiceEditCard;
